if (!window.customElements.get('load-more')) {
  const STORAGE_PREFIX = 'load-more:';

  class LoadMore extends HTMLElement {
    connectedCallback() {
      this.countTarget = this.querySelector('[data-load-more-count]');
      this.total = parseInt(this.getAttribute('data-total'), 10) || 0;
      this.pageSize = parseInt(this.getAttribute('data-page-size'), 10) || 0;
      this.currentPage = parseInt(this.getAttribute('data-current-page'), 10) || 1;
      this.firstPage = this.currentPage;
      this.lastPage = this.currentPage;
      this.countTemplate = this.getAttribute('data-count-template') || '';
      this.loading = false;

      this.tagPage(this.productList ? Array.from(this.productList.children) : [], this.currentPage);
      this.bindTrigger();
      this.bindStateTracking();
      this.restore();
    }

    disconnectedCallback() {
      if (this.onDocumentClick) {
        document.removeEventListener('click', this.onDocumentClick, true);
      }

      if (this.onPageHide) {
        window.removeEventListener('pagehide', this.onPageHide);
      }
    }

    bindTrigger() {
      this.trigger = this.querySelector('.load-more__trigger');
      if (this.trigger && !this.trigger.dataset.bound) {
        this.trigger.dataset.bound = 'true';
        this.trigger.addEventListener('click', this.onClick.bind(this));
      }
    }

    get productList() {
      const section = this.closest('.shopify-section') || document;
      return section.querySelector('product-list');
    }

    /**
     * Records which page each item belongs to, so that leaving through a product can point the URL
     * at the page that product came from.
     */
    tagPage(nodes, page) {
      nodes.forEach((node) => node.setAttribute('data-load-more-page', page));
    }

    /**
     * Keeps track of what the visitor has on screen so it can be rebuilt when they come back: the
     * browser only ever hands back the HTML of a single page.
     */
    bindStateTracking() {
      this.onDocumentClick = (event) => {
        const link = event.target.closest && event.target.closest('a[href]');
        if (!link) {
          return;
        }

        const card = link.closest('product-card');
        if (!card || !this.productList || !this.productList.contains(card)) {
          return;
        }

        this.lastClickedHandle = card.getAttribute('handle');

        // Point the URL at the page this product was rendered on. Coming back then serves a
        // document that already contains it, so it can be scrolled to before the first paint,
        // with the surrounding pages filled in afterwards.
        this.setUrlPage(parseInt(card.getAttribute('data-load-more-page'), 10) || this.currentPage);
        this.saveState();
      };

      this.onPageHide = () => this.saveState();

      document.addEventListener('click', this.onDocumentClick, true);
      window.addEventListener('pagehide', this.onPageHide);
    }

    setUrlPage(page) {
      try {
        const url = new URL(window.location.href);

        if (page > 1) {
          url.searchParams.set('page', page);
        } else {
          url.searchParams.delete('page');
        }

        if (url.href !== window.location.href) {
          history.replaceState(history.state, '', url.href);
        }
      } catch (e) {
        // A tidy URL is a nice-to-have, the session state below is what does the work.
      }
    }

    get stateKey() {
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      url.searchParams.delete('section_id');

      return `${STORAGE_PREFIX}${this.getAttribute('data-section-id')}:${url.pathname}${url.search}`;
    }

    saveState() {
      try {
        window.sessionStorage.setItem(
          this.stateKey,
          JSON.stringify({
            sectionId: this.getAttribute('data-section-id'),
            pageSize: this.pageSize,
            firstPage: this.firstPage,
            lastPage: this.lastPage,
            handle: this.lastClickedHandle || null,
            // Where the pages get fetched from on the way back. The document that comes back isn't
            // always the one that renders this list (on search the products are a separate request),
            // so its own URL can't be used for that.
            pageUrl: this.getAttribute('data-page-url') || null,
            scrollY: Math.round(window.scrollY),
            // Used to hold the same document height on the way back, so the first paint can be put
            // straight onto the saved position instead of drifting into it as products arrive.
            docHeight: document.documentElement.scrollHeight,
            viewportWidth: window.innerWidth,
            savedAt: Date.now(),
          })
        );
      } catch (e) {
        // Private mode or a full quota: restoring is a nice-to-have.
      }
    }

    async fetchDocument(url) {
      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error('Failed to load page');
        }

        return this.parse(await response.text());
      } catch (e) {
        return null;
      }
    }

    parse(html) {
      return html ? new DOMParser().parseFromString(html, 'text/html') : null;
    }

    /**
     * Moves the items of a fetched page into the current list. `anchorNode` inserts them before an
     * existing item (used when filling in earlier pages), otherwise they go to the end.
     */
    insertPage(doc, page, anchorNode) {
      const newList = doc.querySelector('product-list');
      const currentList = this.productList;

      if (!newList || !currentList || newList.children.length === 0) {
        return null;
      }

      const fragment = document.createDocumentFragment();
      const items = [];

      Array.from(newList.children).forEach((node) => {
        const imported = document.importNode(node, true);
        imported.querySelectorAll('[reveal-on-scroll]').forEach((el) => el.removeAttribute('reveal-on-scroll'));
        imported.removeAttribute('reveal-on-scroll');
        fragment.appendChild(imported);
        items.push(imported);
      });

      this.tagPage(items, page);

      if (anchorNode) {
        currentList.insertBefore(fragment, anchorNode);
      } else {
        currentList.appendChild(fragment);
      }

      return items[0];
    }

    syncNext(doc) {
      const newLoadMore = doc.querySelector('load-more');
      const nextUrl = newLoadMore ? newLoadMore.getAttribute('data-next-url') : null;

      if (nextUrl) {
        this.setAttribute('data-next-url', nextUrl);
        return;
      }

      this.removeAttribute('data-next-url');
      const wrapper = this.querySelector('[data-load-more-button-wrapper]');
      if (wrapper) {
        wrapper.remove();
      }
      this.trigger = null;
    }

    async onClick(event) {
      event.preventDefault();

      const nextUrl = this.getAttribute('data-next-url');
      if (!nextUrl || this.loading) {
        return;
      }

      this.loading = true;
      this.trigger.setAttribute('aria-busy', 'true');

      try {
        const url = new URL(nextUrl, window.location.origin);
        url.searchParams.set('section_id', this.getAttribute('data-section-id'));

        const doc = await this.fetchDocument(url.toString());
        if (!doc) {
          throw new Error('Failed to load next page');
        }

        const nextPage = this.lastPage + 1;
        const firstAppended = this.insertPage(doc, nextPage);

        if (firstAppended) {
          const anchor = firstAppended.querySelector('a, button') || firstAppended;
          anchor.setAttribute('tabindex', '-1');
          anchor.focus({ preventScroll: true });
        }

        this.lastPage = nextPage;
        this.syncNext(doc);
        this.updateCount();
        this.saveState();
      } catch (e) {
        window.location.href = nextUrl;
      } finally {
        this.loading = false;
        if (this.trigger) {
          this.trigger.removeAttribute('aria-busy');
        }
      }
    }

    /**
     * Fills the pages the visitor had loaded back in around the page the server rendered. The
     * bootstrap in `snippets/load-more-restore.liquid` has already started the requests and put the
     * page on the right product, so the work here happens around a fixed viewport.
     */
    async restore() {
      const restore = window.loadMoreRestore;

      if (!restore || restore.consumed || !this.productList) {
        return;
      }

      restore.consumed = true;
      this.loading = true;
      this.lastClickedHandle = restore.state.handle;
      this.toggleAttribute('data-restoring', true);

      try {
        const anchorNode = this.productList.firstElementChild;

        for (const item of restore.before) {
          const doc = this.parse(await item.html);
          if (!doc) {
            break;
          }

          // With the height reserved, the space these pages take up is already accounted for and the
          // scroll position must not move. Otherwise they land above the visitor and push everything
          // down, so the position is corrected by however tall they turn out to be.
          const anchorCard = restore.reserved ? null : restore.card() || anchorNode;
          const offsetBefore = anchorCard ? anchorCard.getBoundingClientRect().top : null;

          if (!this.insertPage(doc, item.page, anchorNode)) {
            break;
          }

          if (anchorCard && offsetBefore !== null && !restore.cancelled) {
            window.scrollBy(0, anchorCard.getBoundingClientRect().top - offsetBefore);
          }

          this.firstPage = item.page;
          this.updateCount();
        }

        // The earlier pages are in, so the exact position can be taken from the card again.
        restore.holdScroll = false;
        restore.align();

        // Later pages go below, so they can't move anything the visitor is looking at.
        for (const item of restore.after) {
          const doc = this.parse(await item.html);
          if (!doc || !this.insertPage(doc, item.page)) {
            break;
          }

          this.lastPage = item.page;
          this.syncNext(doc);
          this.updateCount();
        }
      } finally {
        this.loading = false;
        this.removeAttribute('data-restoring');
        restore.align();
        restore.finish();
      }
    }

    updateCount() {
      if (!this.countTarget || !this.countTemplate) {
        return;
      }

      const currentList = this.productList;
      const rendered = currentList ? currentList.querySelectorAll('product-card').length : 0;
      const shown = Math.min((this.firstPage - 1) * this.pageSize + rendered, this.total);

      this.countTarget.textContent = this.countTemplate
        .replace('__count__', shown)
        .replace('__total__', this.total);
    }
  }

  window.customElements.define('load-more', LoadMore);
}
